/**
 * ActivityTimelineCard — a cross-system customer timeline that merges orders,
 * loyalty points, chat, and storefront browsing (PostHog) into one feed.
 *
 * Design goals (calm, iPad-Settings-like — not a dense enterprise log):
 *  - A real vertical timeline RAIL (connecting line + nodes), grouped by day.
 *  - RELATIVE time on every row so order-within-a-day is legible.
 *  - Browsing bursts collapse into a single "session" row (N pages · added to
 *    cart) instead of a wall of individual page-views.
 *  - A FILTER segmented control (All / Orders / Browsing / Chat / Points).
 *  - LIFECYCLE milestones (First order, Customer since) anchor the bottom.
 *
 * Everything is read-only and derived from the permission-checked Customer doc.
 * Chat is LINE-only; browsing is best-effort (empty when PostHog is off).
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import {
  Activity, MessageCircle, Eye, Clock, ShoppingBag, ShoppingCart, Star, Flag,
  Sparkles, UserPlus, CreditCard, LayoutGrid, UserCircle, Tag, Receipt, Award,
  Gift, Cake, CalendarCheck, Wallet, ChevronDown,
} from "lucide-react";
import { CARD } from "@/config/design-tokens";
import { cn } from "@/lib/utils";
import { routes } from "@/lib/config/routes";
import { useCustomerOrders, useCustomerAvatar, type CustomerBrowsingEvent } from "@/features/customers/api/customerHooks";
import { fetchCustomerChatSummary } from "@/features/customers/api/customerConversation";
import { useCustomerPointHistory } from "@/features/rewards/api/rewardsAdminHooks";
import { useChatPanelStore } from "@/lib/store/chatPanelStore";
import { CardTitle, EmptyValue, formatMoney, formatShortDate, timeAgo, parseTs } from "./customerDetailPrimitives";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ActivityKind = "order" | "points" | "chat" | "view" | "cart" | "session" | "milestone";
/** Filter buckets — milestones live outside filtering (shown only under "all"). */
type FilterKey = "all" | "orders" | "browsing" | "chat" | "points";

interface ActivityRow {
  ts: string;
  kind: ActivityKind;
  filter: Exclude<FilterKey, "all"> | "milestone";
  title: string;
  sub?: React.ReactNode;
  to?: string;
  onClick?: () => void;
  Icon?: LucideIcon;
  /** Real thumbnail (product / avatar / collection) shown instead of the icon. */
  thumbUrl?: string;
  thumbShape?: "circle" | "square";
  /** Milestones render lighter, as anchor points rather than events. */
  muted?: boolean;
  /** Stable id for expand/collapse state (grouped rows only). */
  id?: string;
  /** Child rows revealed when a grouped row (e.g. a browsing session) expands. */
  children?: ActivityRow[];
}

// ---------------------------------------------------------------------------
// Icon + label helpers
// ---------------------------------------------------------------------------

function activityIcon(kind: ActivityKind): LucideIcon {
  switch (kind) {
    case "order": return ShoppingBag;
    case "points": return Star;
    case "chat": return MessageCircle;
    case "cart": return ShoppingCart;
    case "session": return Eye;
    case "milestone": return Sparkles;
    default: return Eye;
  }
}

/** "Today" / "Yesterday" / "7 Aug" for a timestamp's day. */
export function dayLabel(ts: string): string {
  const day = (ts || "").split(/[ T]/)[0];
  if (!day) return "";
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const key = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (day === key(now)) return "Today";
  if (day === key(new Date(now.getTime() - 86400000))) return "Yesterday";
  return formatShortDate(day);
}

const pretty = (s: string) =>
  s.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();

/** Clean title + a page-specific icon for a single PostHog browsing event. */
function browsingMeta(e: CustomerBrowsingEvent): { title: string; Icon: LucideIcon } {
  if (e.event === "$autocapture") return { title: "Added to cart", Icon: ShoppingCart };
  const path = (e.path || "").split("?")[0].replace(/\/+$/, "");
  const segs = path.split("/").filter(Boolean);
  if (!segs.length) return { title: "Visited home", Icon: Eye };
  const last = segs[segs.length - 1];
  const first = segs[0].toLowerCase();
  if (path.includes("/product")) return { title: `Viewed ${pretty(last)}`, Icon: Tag };
  if (first === "order" || first === "orders") return { title: `Viewed order ${last.toUpperCase()}`, Icon: Receipt };
  if (first === "checkout") return { title: "Visited checkout", Icon: CreditCard };
  if (first === "collection" || first === "collections") return { title: "Browsed collections", Icon: LayoutGrid };
  if (first === "account") return { title: "Visited account", Icon: UserCircle };
  if (first === "cart") return { title: "Viewed cart", Icon: ShoppingCart };
  if (first === "reviews") return { title: "Viewed reviews", Icon: Star };
  if (first === "rewards") return { title: "Viewed rewards", Icon: Award };
  return { title: `Visited ${pretty(first)}`, Icon: Eye };
}

export function pointsIcon(source: string): LucideIcon {
  switch ((source || "").toLowerCase()) {
    case "signup": return UserPlus;
    case "birthday": return Cake;
    case "referral": return Gift;
    case "daily check-in": return CalendarCheck;
    case "purchase": return Wallet;
    default: return Star;
  }
}

// ---------------------------------------------------------------------------
// Browsing → sessions
// ---------------------------------------------------------------------------

const SESSION_GAP_MS = 30 * 60 * 1000; // events >30min apart start a new session

/**
 * Group a customer's browsing events into sessions. A lone event stays a single
 * "view"/"cart" row; a burst (≥2 events within 30-min gaps) collapses into one
 * "session" row summarising page count + whether it ended in an add-to-cart.
 */
function buildBrowsingRows(
  browsing: CustomerBrowsingEvent[] | undefined,
  ctx: { avatarUrl?: string | null; orderThumbs?: Record<string, string>; collectionThumbs?: Record<string, string> },
): ActivityRow[] {
  const evs = (browsing ?? []).filter((e) => e.at);
  if (!evs.length) return [];
  // Oldest→newest for gap detection.
  const sorted = [...evs].sort((a, b) => (a.at! < b.at! ? -1 : 1));
  const sessions: CustomerBrowsingEvent[][] = [];
  for (const e of sorted) {
    const cur = sessions[sessions.length - 1];
    const prevTs = cur && parseTs(cur[cur.length - 1].at)?.getTime();
    const curTs = parseTs(e.at)?.getTime();
    if (cur && prevTs != null && curTs != null && curTs - prevTs <= SESSION_GAP_MS) cur.push(e);
    else sessions.push([e]);
  }

  const rows: ActivityRow[] = [];
  for (const s of sessions) {
    const last = s[s.length - 1]; // most recent event in the session
    if (s.length === 1) {
      rows.push(singleBrowsingRow(last, ctx));
      continue;
    }
    const views = s.filter((e) => e.event !== "$autocapture").length;
    const carts = s.filter((e) => e.event === "$autocapture").length;
    // Prefer a real product/collection thumb from any event in the session.
    let thumbUrl: string | undefined;
    for (const e of [...s].reverse()) {
      const t = thumbFor(e, ctx);
      if (t) { thumbUrl = t; break; }
    }
    const parts = [`${s.length} page${s.length > 1 ? "s" : ""}`];
    if (carts) parts.push(`added ${carts} to cart`);
    rows.push({
      ts: last.at!,
      kind: "session",
      filter: "browsing",
      title: "Browsing session",
      sub: parts.join(" · "),
      Icon: carts ? ShoppingCart : Eye,
      thumbUrl,
      thumbShape: "square",
      id: `sess-${last.at}-${s.length}`,
      // Individual pages, newest first — revealed when the session is expanded.
      children: [...s].reverse().map((e) => singleBrowsingRow(e, ctx)),
    });
    void views;
  }
  return rows;
}

/** Resolve a thumbnail for one browsing event, if any (collection/order/avatar). */
function thumbFor(
  e: CustomerBrowsingEvent,
  ctx: { avatarUrl?: string | null; orderThumbs?: Record<string, string>; collectionThumbs?: Record<string, string> },
): string | undefined {
  const segs = (e.path || "").split("?")[0].replace(/\/+$/, "").split("/").filter(Boolean);
  const first = (segs[0] || "").toLowerCase();
  const collSlug = ctx.collectionThumbs
    ? segs.map((x) => x.toLowerCase()).find((x) => ctx.collectionThumbs![x])
    : undefined;
  if (collSlug) return ctx.collectionThumbs![collSlug];
  if ((first === "order" || first === "orders") && segs.length > 1)
    return ctx.orderThumbs?.[segs[segs.length - 1].toUpperCase()];
  if (first === "account") return ctx.avatarUrl || undefined;
  return undefined;
}

function singleBrowsingRow(
  e: CustomerBrowsingEvent,
  ctx: { avatarUrl?: string | null; orderThumbs?: Record<string, string>; collectionThumbs?: Record<string, string> },
): ActivityRow {
  const meta = browsingMeta(e);
  const segs = (e.path || "").split("?")[0].replace(/\/+$/, "").split("/").filter(Boolean);
  const first = (segs[0] || "").toLowerCase();
  const thumbUrl = thumbFor(e, ctx);
  const collSlug = ctx.collectionThumbs
    ? segs.map((x) => x.toLowerCase()).find((x) => ctx.collectionThumbs![x])
    : undefined;
  const title = collSlug ? `Browsed ${pretty(collSlug)}` : meta.title;
  return {
    ts: e.at!,
    kind: e.event === "$autocapture" ? "cart" : "view",
    filter: "browsing",
    title,
    sub: "Storefront",
    Icon: meta.Icon,
    thumbUrl,
    thumbShape: first === "account" ? "circle" : "square",
  };
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "orders", label: "Orders" },
  { key: "browsing", label: "Browsing" },
  { key: "chat", label: "Chat" },
  { key: "points", label: "Points" },
];

export function ActivityTimelineCard({
  customer,
  currency,
  lineUserId,
  browsing,
  lastSeen,
  customerSince,
  orderThumbs,
  collectionThumbs,
}: {
  customer: string;
  currency: string;
  lineUserId?: string;
  browsing?: CustomerBrowsingEvent[];
  lastSeen?: string | null;
  /** Customer.creation — anchors the "Customer since" milestone. */
  customerSince?: string | null;
  orderThumbs?: Record<string, string>;
  collectionThumbs?: Record<string, string>;
}) {
  const [filter, setFilter] = useState<FilterKey>("all");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const openChatPanel = useChatPanelStore((s) => s.openConversation);
  const { data: avatarUrl } = useCustomerAvatar(customer);
  const { data: orders, isLoading: ordersLoading } = useCustomerOrders(customer);
  const { data: points, isLoading: pointsLoading } = useCustomerPointHistory(customer, 50, 0, !!customer);
  const { data: chat } = useQuery({
    queryKey: ["customers", "chat-summary", lineUserId],
    queryFn: () => fetchCustomerChatSummary({ lineUserId, limit: 5 }),
    enabled: !!lineUserId,
    staleTime: 60_000,
  });
  const isLoading = ordersLoading || pointsLoading;

  const items = useMemo<ActivityRow[]>(() => {
    const out: ActivityRow[] = [];

    for (const o of orders ?? []) {
      const rec = o as Record<string, any>;
      out.push({
        ts: (rec.creation || rec.transaction_date || "") as string,
        kind: "order",
        filter: "orders",
        title: rec.name,
        sub: (
          <span>
            <span className="font-medium text-foreground">
              {formatMoney(rec.grand_total, rec.currency || currency)}
            </span>
            {rec.status ? ` · ${rec.status}` : ""}
          </span>
        ),
        thumbUrl: orderThumbs?.[rec.name],
        thumbShape: "square",
        to: routes.salesOrder.detail(rec.name),
      });
    }

    for (const e of points?.entries ?? []) {
      if (Number(e.points) === 0) continue;
      const positive = Number(e.points) > 0;
      out.push({
        ts: (e.posting_date || "") as string,
        kind: "points",
        filter: "points",
        title: e.earn_source || "Loyalty",
        sub: (
          <span>
            <span className={cn("font-mono font-semibold", positive ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400")}>
              {positive ? "+" : ""}{Number(e.points).toLocaleString()}
            </span>
            {e.description || e.invoice ? ` · ${e.description || e.invoice}` : ""}
          </span>
        ),
        Icon: pointsIcon(e.earn_source || ""),
      });
    }

    if (chat?.found && chat.messages?.length) {
      const acc = chat.account_id;
      const conv = chat.conversation_id;
      const msgs = chat.messages.filter((m) => m.content);
      if (msgs.length) {
        const latest = msgs[0]; // most-recent first
        out.push({
          ts: latest.at || "",
          kind: "chat",
          filter: "chat",
          title: latest.content,
          sub: msgs.length > 1 ? `Chat · ${msgs.length} messages` : latest.incoming ? "Customer message" : "Your reply",
          onClick: acc != null && conv != null ? () => openChatPanel(acc, conv) : undefined,
        });
      }
    }

    out.push(...buildBrowsingRows(browsing, { avatarUrl, orderThumbs, collectionThumbs }));

    // Lifecycle milestones (anchor points).
    const firstOrder = (orders ?? []).reduce<Record<string, any> | null>((min, o) => {
      const rec = o as Record<string, any>;
      const t = (rec.creation || rec.transaction_date || "") as string;
      if (!t) return min;
      if (!min || t < (min.creation || min.transaction_date)) return rec;
      return min;
    }, null);
    if (firstOrder) {
      out.push({
        ts: (firstOrder.creation || firstOrder.transaction_date) as string,
        kind: "milestone",
        filter: "milestone",
        title: "First order",
        sub: firstOrder.name,
        Icon: Flag,
        muted: true,
        to: routes.salesOrder.detail(firstOrder.name),
      });
    }
    if (customerSince) {
      out.push({
        ts: customerSince,
        kind: "milestone",
        filter: "milestone",
        title: "Customer since",
        sub: formatShortDate(customerSince),
        Icon: Sparkles,
        muted: true,
      });
    }

    out.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
    return out;
  }, [orders, points, chat, browsing, avatarUrl, orderThumbs, collectionThumbs, customerSince, currency, openChatPanel]);

  // Which filters actually have data (so we never show an empty tab).
  const available = useMemo(() => {
    const s = new Set<FilterKey>(["all"]);
    for (const it of items) if (it.filter !== "milestone") s.add(it.filter);
    return s;
  }, [items]);

  const filtered = useMemo(() => {
    const events = items.filter((it) => it.filter !== "milestone");
    if (filter !== "all") return events.filter((it) => it.filter === filter).slice(0, 15);
    // "All": cap the recent event stream, then always anchor milestones at the
    // bottom (they're the oldest, so concatenation keeps the desc order).
    const milestones = items.filter((it) => it.filter === "milestone");
    return [...events.slice(0, 12), ...milestones];
  }, [items, filter]);

  // Day-group, preserving sort order.
  const groups = useMemo(() => {
    const g: Array<[string, ActivityRow[]]> = [];
    for (const it of filtered) {
      const label = dayLabel(it.ts);
      const last = g[g.length - 1];
      if (last && last[0] === label) last[1].push(it);
      else g.push([label, [it]]);
    }
    return g;
  }, [filtered]);

  return (
    <div className={CARD}>
      <div className="p-5">
        <div className="flex items-center justify-between">
          <CardTitle icon={<Activity className="h-3.5 w-3.5" />}>Activity</CardTitle>
          {lastSeen && (
            <div className="-mt-3 flex items-center gap-1 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3" />
              Last seen {timeAgo(lastSeen)}
            </div>
          )}
        </div>

        {/* Filter segmented control (only categories that have data) */}
        {available.size > 2 && (
          <div className="mb-4 inline-flex rounded-lg bg-muted/60 p-0.5">
            {FILTERS.filter((f) => available.has(f.key)).map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                  filter === f.key
                    ? "bg-white text-foreground shadow-sm dark:bg-gray-700"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : groups.length === 0 ? (
          <EmptyValue />
        ) : (
          <div className="space-y-6">
            {groups.map(([day, rows]) => (
              <div key={day}>
                <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {day}
                </div>
                {/* Timeline rail: continuous line behind the nodes for this day */}
                <div>
                  {rows.map((it, i) => {
                    const Icon = it.Icon ?? activityIcon(it.kind);
                    const isFirst = i === 0;
                    const isLast = i === rows.length - 1;
                    const isExpandable = !!(it.id && it.children?.length);
                    const isOpen = isExpandable && expanded.has(it.id!);
                    const node = it.thumbUrl ? (
                      <img
                        src={it.thumbUrl}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        className={cn(
                          "h-9 w-9 object-cover ring-1 ring-black/5 dark:ring-white/10",
                          it.thumbShape === "square" ? "rounded-lg" : "rounded-full",
                        )}
                      />
                    ) : (
                      <span
                        className={cn(
                          "flex h-9 w-9 items-center justify-center rounded-full",
                          it.muted
                            ? "bg-background text-gray-400 border border-dashed border-gray-300 dark:border-gray-600"
                            : "bg-muted text-muted-foreground",
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                    );

                    const content = (
                      <div className={cn("min-w-0 flex-1 pb-4", isLast && !isOpen && "pb-0")}>
                        <div className="flex items-center justify-between gap-2">
                          <span className={cn("truncate text-sm font-medium", it.muted ? "text-muted-foreground" : "text-foreground")}>
                            {it.title}
                          </span>
                          <span className="flex shrink-0 items-center gap-1">
                            <span className="text-xs tabular-nums text-gray-400 dark:text-gray-500">
                              {timeAgo(it.ts)}
                            </span>
                            {isExpandable && (
                              <ChevronDown
                                className={cn(
                                  "h-3.5 w-3.5 text-gray-400 transition-transform",
                                  isOpen && "rotate-180",
                                )}
                              />
                            )}
                          </span>
                        </div>
                        {it.sub && <div className="truncate text-xs text-muted-foreground">{it.sub}</div>}

                        {/* Expanded children (e.g. individual pages of a session) */}
                        {isOpen && it.children && (
                          <div className="mt-2.5 space-y-2 border-l border-border/60 pl-3">
                            {it.children.map((c, ci) => {
                              const CIcon = c.Icon ?? activityIcon(c.kind);
                              return (
                                <div key={ci} className="flex items-center justify-between gap-2">
                                  <div className="flex min-w-0 items-center gap-2">
                                    {c.thumbUrl ? (
                                      <img
                                        src={c.thumbUrl}
                                        alt=""
                                        loading="lazy"
                                        referrerPolicy="no-referrer"
                                        className={cn(
                                          "h-5 w-5 shrink-0 object-cover ring-1 ring-black/5 dark:ring-white/10",
                                          c.thumbShape === "square" ? "rounded" : "rounded-full",
                                        )}
                                      />
                                    ) : (
                                      <CIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                    )}
                                    <span className="truncate text-xs text-foreground/90">{c.title}</span>
                                  </div>
                                  <span className="shrink-0 text-[11px] tabular-nums text-gray-400 dark:text-gray-500">
                                    {timeAgo(c.ts)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );

                    const rowInner = (
                      <div className="flex items-start gap-3.5">
                        {/* Node column with the connecting rail */}
                        <div className="relative flex w-9 shrink-0 justify-center">
                          <span
                            className={cn(
                              "absolute left-1/2 w-px -translate-x-1/2 bg-gray-200 dark:bg-gray-700",
                              isFirst ? "top-[18px]" : "top-0",
                              isLast && !isOpen ? "h-[18px]" : "bottom-0",
                            )}
                          />
                          <span className="relative z-10">{node}</span>
                        </div>
                        {content}
                      </div>
                    );

                    const rowClass = "block rounded-lg -mx-2 px-2 transition-colors";
                    if (isExpandable) {
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => toggleExpand(it.id!)}
                          className={cn(rowClass, "w-[calc(100%_+_1rem)] text-left hover:bg-muted/50")}
                        >
                          {rowInner}
                        </button>
                      );
                    }
                    if (it.onClick) {
                      return (
                        <button key={i} type="button" onClick={it.onClick} className={cn(rowClass, "w-[calc(100%_+_1rem)] text-left hover:bg-muted/50")}>
                          {rowInner}
                        </button>
                      );
                    }
                    return it.to ? (
                      <Link key={i} to={it.to} className={cn(rowClass, "hover:bg-muted/50")}>
                        {rowInner}
                      </Link>
                    ) : (
                      <div key={i} className="px-0">{rowInner}</div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
